<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  formatDateTime,
  identityConfidenceLabel,
  identityConflictLabel,
  identityFieldLabels,
  identityHistoryDateLabel,
  identitySourceLabel,
} from '../utils/imovelShowFormatters';

defineProps({
  statusLabel: {
    type: String,
    default: '',
  },
  statusTone: {
    type: String,
    default: 'neutral',
  },
  successMessage: {
    type: String,
    default: '',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  permissionDenied: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
  empty: {
    type: Boolean,
    default: false,
  },
  actionableConflicts: {
    type: Array,
    default: () => [],
  },
  canonicalAddressLine: {
    type: String,
    default: '',
  },
  usesLegacyProjection: {
    type: Boolean,
    default: false,
  },
  canonicalRows: {
    type: Array,
    default: () => [],
  },
  aliases: {
    type: Array,
    default: () => [],
  },
  sourceLinks: {
    type: Array,
    default: () => [],
  },
  conflicts: {
    type: Array,
    default: () => [],
  },
  changeHistory: {
    type: Array,
    default: () => [],
  },
  mergeHistory: {
    type: Array,
    default: () => [],
  },
  aliasError: {
    type: String,
    default: '',
  },
  aliasSaving: {
    type: Boolean,
    default: false,
  },
  canSubmitAlias: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['reload', 'openDuplicateQueue', 'submitAlias']);

const aliasForm = defineModel('aliasForm', {
  type: Object,
  required: true,
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard
    title="Identidade física canônica"
    prefix-icon="i-lucide-fingerprint"
  >
    <template #titleAction>
      <LaisStatusBadge :label="statusLabel" :tone="statusTone" size="sm" dot />
    </template>

    <div
      v-if="successMessage"
      class="mb-3 rounded-lais-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] font-semibold text-emerald-800"
    >
      {{ successMessage }}
    </div>

    <div
      v-if="loading"
      class="flex items-center gap-2 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-3 text-[13px] text-lais-text-body"
      aria-live="polite"
    >
      <span class="i-lucide-loader-2 size-4 animate-spin" aria-hidden="true" />
      Carregando identidade física...
    </div>
    <div
      v-else-if="permissionDenied"
      class="rounded-lais-sm border border-lais-danger/40 bg-lais-danger-50 px-3 py-3 text-[13px] leading-relaxed text-lais-danger"
    >
      {{ error }}
    </div>
    <div
      v-else-if="error"
      class="grid gap-3 rounded-lais-sm border border-red-200 bg-red-50 px-3 py-3 text-[13px] leading-relaxed text-red-700"
    >
      <p>{{ error }}</p>
      <LaisPrimaryButton
        class="w-fit"
        variant="ghost"
        size="sm"
        icon="i-lucide-refresh-cw"
        @click="emit('reload')"
      >
        Recarregar identidade
      </LaisPrimaryButton>
    </div>
    <div
      v-else-if="empty"
      class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-3 text-[13px] leading-relaxed text-lais-text-body"
    >
      Nenhuma identidade física normalizada foi encontrada para este imóvel.
    </div>
    <div v-else class="grid gap-4">
      <div
        v-if="actionableConflicts.length"
        class="flex flex-col gap-3 rounded-lais-sm border border-amber-200 bg-amber-50 px-3 py-3 text-[13px] leading-relaxed text-amber-900 sm:flex-row sm:items-center sm:justify-between"
      >
        <p>
          Existem {{ actionableConflicts.length }} conflitos de identidade
          pendentes de revisão.
        </p>
        <LaisPrimaryButton
          class="w-fit shrink-0"
          variant="ghost"
          size="sm"
          icon="i-lucide-list-checks"
          @click="emit('openDuplicateQueue')"
        >
          Revisar conflitos
        </LaisPrimaryButton>
      </div>

      <section
        class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft p-4"
      >
        <div class="flex flex-wrap items-center justify-between gap-2">
          <p class="text-[13px] font-semibold text-lais-text">
            Endereço normalizado
          </p>
          <LaisStatusBadge
            v-if="usesLegacyProjection"
            label="Projeção legada"
            tone="warning"
            size="sm"
            dot
          />
        </div>
        <p class="mt-2 text-[14px] leading-relaxed text-lais-text">
          {{ canonicalAddressLine || 'Sem endereço normalizado' }}
        </p>
        <dl
          v-if="canonicalRows.length"
          class="mt-3 grid gap-2 text-[12px] text-lais-text-body sm:grid-cols-2"
        >
          <div
            v-for="row in canonicalRows"
            :key="row.label"
            class="min-w-0 rounded-lais-sm bg-white px-3 py-2"
          >
            <dt class="text-lais-text-muted">{{ row.label }}</dt>
            <dd class="mt-0.5 break-all font-semibold text-lais-text">
              {{ row.value }}
            </dd>
          </div>
        </dl>
      </section>

      <section v-if="aliases.length" class="grid gap-2">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <p class="text-[13px] font-semibold text-lais-text">
            Aliases externos
          </p>
          <span class="text-[12px] text-lais-text-muted">
            Alias não substitui o canônico sem revisão
          </span>
        </div>
        <ul class="grid gap-2">
          <li
            v-for="alias in aliases"
            :key="alias.id || `${alias.source}-${alias.externalId}`"
            class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-2 text-[13px]"
          >
            <div
              class="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between"
            >
              <div class="min-w-0">
                <p class="font-semibold text-lais-text">
                  {{ identitySourceLabel(alias.source) }}
                </p>
                <p class="break-all text-lais-text-body">
                  {{ alias.externalId }}
                </p>
              </div>
              <span class="text-[12px] text-lais-text-muted">
                visto em {{ formatDateTime(alias.lastSeenAt) }}
              </span>
            </div>
            <a
              v-if="alias.externalUrl"
              :href="alias.externalUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="mt-1 inline-flex break-all text-[12px] font-semibold text-lais-link"
            >
              {{ alias.externalUrl }}
            </a>
          </li>
        </ul>
      </section>

      <section v-if="sourceLinks.length" class="grid gap-2">
        <p class="text-[13px] font-semibold text-lais-text">
          Ligações com listings e fontes externas
        </p>
        <ul class="grid gap-2 sm:grid-cols-2">
          <li
            v-for="link in sourceLinks"
            :key="link.key"
            class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-2 text-[12px] text-lais-text-body"
          >
            <p class="font-semibold text-lais-text">{{ link.kind }}</p>
            <p>{{ link.source }}</p>
            <p v-if="link.reference" class="break-all">
              {{ link.reference }}
            </p>
            <LaisStatusBadge
              v-if="link.status"
              class="mt-2"
              :label="link.status"
              tone="neutral"
              size="sm"
            />
          </li>
        </ul>
      </section>

      <section v-if="conflicts.length" class="grid gap-2">
        <p class="text-[13px] font-semibold text-lais-text">
          Conflitos detectados
        </p>
        <ul class="grid gap-2">
          <li
            v-for="candidate in conflicts"
            :key="candidate.id"
            class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-2 text-[13px] text-lais-text-body"
          >
            <div class="flex flex-wrap items-center justify-between gap-2">
              <p class="font-semibold text-lais-text">
                {{ identityConflictLabel(candidate) }}
              </p>
              <LaisStatusBadge
                :label="
                  candidate.requiresHumanReview
                    ? 'Revisão humana'
                    : candidate.status
                "
                :tone="
                  candidate.requiresHumanReview ? 'warning' : candidate.status
                "
                size="sm"
                dot
              />
            </div>
            <p class="mt-1">
              Imóvel relacionado #{{ candidate.otherImovelId }} · score
              {{ identityConfidenceLabel(candidate.matchScore) }}
            </p>
          </li>
        </ul>
      </section>

      <section v-if="changeHistory.length" class="grid gap-2">
        <p class="text-[13px] font-semibold text-lais-text">
          Histórico de identidade
        </p>
        <ol class="grid gap-2">
          <li
            v-for="entry in changeHistory.slice(0, 5)"
            :key="entry.id || entry.sequence"
            class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-2 text-[13px] text-lais-text-body"
          >
            <div class="flex flex-wrap items-start justify-between gap-2">
              <div class="min-w-0">
                <p class="font-semibold text-lais-text">
                  {{ entry.eventLabel || entry.eventType }}
                </p>
                <p class="mt-0.5">
                  {{ identitySourceLabel(entry.source) }} ·
                  {{ entry.actor?.name || 'Sistema' }}
                </p>
              </div>
              <span class="text-[12px] text-lais-text-muted">
                {{ identityHistoryDateLabel(entry) }}
              </span>
            </div>
            <p class="mt-1 text-[12px]">
              Campos: {{ identityFieldLabels(entry) }}
            </p>
          </li>
        </ol>
      </section>

      <section v-if="mergeHistory.length" class="grid gap-2">
        <p class="text-[13px] font-semibold text-lais-text">
          Histórico de mesclagens
        </p>
        <ul class="grid gap-2">
          <li
            v-for="merge in mergeHistory.slice(0, 3)"
            :key="merge.id"
            class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-2 text-[13px] text-lais-text-body"
          >
            {{ merge.role }} · {{ merge.status }} · vencedor #{{
              merge.winnerImovelId
            }}
            / substituído #{{ merge.loserImovelId }}
          </li>
        </ul>
      </section>

      <form
        class="grid gap-3 rounded-lais-sm border border-lais-border-soft bg-white p-4"
        @submit.prevent="emit('submitAlias')"
      >
        <div>
          <p class="text-[13px] font-semibold text-lais-text">
            Registrar alias externo
          </p>
          <p class="mt-1 text-[12px] leading-relaxed text-lais-text-muted">
            O alias fica vinculado à identidade física e não promove novo
            endereço canônico sem revisão.
          </p>
        </div>
        <div class="grid gap-3 md:grid-cols-3">
          <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
            Fonte
            <input
              v-model="aliasForm.source"
              type="text"
              class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
              placeholder="Ex: olx"
            />
          </label>
          <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
            Código externo
            <input
              v-model="aliasForm.external_id"
              type="text"
              class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
              placeholder="Ex: 12345"
            />
          </label>
          <label class="grid gap-1 text-[12px] font-semibold text-lais-text">
            URL
            <input
              v-model="aliasForm.external_url"
              type="url"
              class="h-10 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] font-normal text-lais-text focus:border-lais-primary focus:outline-none"
              placeholder="https://"
            />
          </label>
        </div>
        <p
          v-if="aliasError"
          class="rounded-lais-sm bg-red-50 px-3 py-2 text-[12px] text-red-700"
        >
          {{ aliasError }}
        </p>
        <LaisPrimaryButton
          class="w-fit"
          type="submit"
          size="sm"
          icon="i-lucide-link"
          :is-loading="aliasSaving"
          :disabled="!canSubmitAlias || permissionDenied"
        >
          Registrar alias
        </LaisPrimaryButton>
      </form>
    </div>
  </LaisCard>
</template>
